import { BASE, ApiError, request } from './_core'
import type {
  ThemeSummary,
  ThemeDetail,
  ThemeImage,
  ThemeGenerationStatus,
  ImageGenerationStatus,
} from './activities'

// Re-export types consumed by themes so importers can get them from here too
export type { ThemeSummary, ThemeDetail, ThemeImage, ThemeGenerationStatus, ImageGenerationStatus }

// ── Themes ───────────────────────────────────────────────────

export interface AddImageParams {
  url:          string
  s3Key:        string
  wordEs:       string
  wordEn?:      string
  altEs?:       string
  altEn?:       string
  definitionEs?: string
  definitionEn?: string
  value?:       number
  sortOrder?:   number
}

export interface UpdateImageParams {
  url?:          string
  s3Key?:        string
  altEs?:        string
  altEn?:        string
  wordEs?:       string
  wordEn?:       string
  definitionEs?: string
  definitionEn?: string
  value?:        number | null
}

export interface GenerateThemeResult {
  id:               number
  slug:             string
  generationStatus: ThemeGenerationStatus
}

export interface RegenerateImageResult {
  id:               number
  generationStatus: ImageGenerationStatus
}

export const themes = {
  list:    (activitySlug?: string)                    => request<ThemeSummary[]>(activitySlug ? `/themes?activitySlug=${encodeURIComponent(activitySlug)}` : '/themes'),
  listAll: ()                                         => request<ThemeSummary[]>('/themes?all=true'),
  get:     (slug: string)                             => request<ThemeDetail>(`/themes/${slug}`),
  create:  (data: { slug: string; nameEs: string; nameEn: string; description?: string; requiredTier?: 'BASIC' | 'PLUS' | 'PREMIUM' }) =>
    request<ThemeDetail>('/themes', { method: 'POST', body: JSON.stringify(data) }),
  update:  (slug: string, data: Partial<{ nameEs: string; nameEn: string; description: string; coverUrl: string; published: boolean; sortOrder: number; requiredTier: 'BASIC' | 'PLUS' | 'PREMIUM' }>) =>
    request<ThemeDetail>(`/themes/${slug}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete:  (slug: string) =>
    request<void>(`/themes/${slug}`, { method: 'DELETE' }),
  generate: (prompt: string) =>
    request<GenerateThemeResult>('/themes/generate', { method: 'POST', body: JSON.stringify({ prompt }) }),
  retry: (slug: string, prompt?: string) =>
    request<GenerateThemeResult>(`/themes/${slug}/retry`, { method: 'POST', body: prompt ? JSON.stringify({ prompt }) : undefined }),
  generateItems: (slug: string, data: { count: number; extraPrompt?: string }) =>
    request<GenerateThemeResult>(`/themes/${slug}/generate-items`, { method: 'POST', body: JSON.stringify(data) }),
  addImage: (slug: string, data: AddImageParams) =>
    request<ThemeImage>(`/themes/${slug}/images`, { method: 'POST', body: JSON.stringify(data) }),
  batchAddImages: (slug: string, images: Array<{ url: string; s3Key: string; wordEs: string; sortOrder?: number }>) =>
    request<ThemeImage[]>(`/themes/${slug}/images/batch`, { method: 'POST', body: JSON.stringify({ images }) }),
  updateImage: (slug: string, id: number, data: UpdateImageParams) =>
    request<ThemeImage>(`/themes/${slug}/images/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteImage: (slug: string, id: number) =>
    request<void>(`/themes/${slug}/images/${id}`, { method: 'DELETE' }),
  regenerateImage: (slug: string, id: number, data: { type: 'texts' | 'definition' | 'image'; refinementPrompt?: string }) =>
    request<RegenerateImageResult>(`/themes/${slug}/images/${id}/regenerate`, { method: 'POST', body: JSON.stringify(data) }),

  export: async (slug: string): Promise<Blob> => {
    const token = localStorage.getItem('access_token')
    const res = await fetch(`${BASE}/themes/${slug}/export`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) throw new ApiError(res.status, res.statusText)
    return res.blob()
  },

  import: (file: File) =>
    request<{ id: number; slug: string; nameEs: string; nameEn: string; published: boolean; generationStatus: string }>(
      '/themes/import',
      { method: 'POST', headers: { 'Content-Type': 'application/zip' }, body: file },
    ),
}
